import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Chip,
  Container,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import apiClient from '../../api/apiClient';
import { extractErrorMessage } from '../../api/extractErrorMessage';
import { getEventTypeLabel } from '../../constants/eventTypes';
import DWLFLoader from '../../components/DWLFLoader';

/*
 * Alert Delivery Log — "did my alert actually send".
 *
 * Every notification the dispatcher attempted for this account's
 * subscriptions, newest-first. A healthy system shows a steady drip of
 * `ok` rows; a silent outage shows up as a gap you can see.
 */

const MONO = '"Space Mono", monospace';
const GROTESK = '"Space Grotesk", sans-serif';
const MUTED = 'var(--dwlf-ink-50)';

const STATUS_CHIP = {
  ok: { label: 'Delivered', color: 'success' },
  failed: { label: 'Failed', color: 'error' },
  rate_limited: { label: 'Rate limited', color: 'warning' },
  pending: { label: 'Pending', color: 'default' },
};

const headCell = { fontFamily: MONO, fontSize: '0.7rem', letterSpacing: '0.06em', textTransform: 'uppercase', color: MUTED, fontWeight: 600 };

const AlertLogPage = () => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    apiClient
      .get('/notifications/history')
      .then((res) => {
        if (!cancelled) {setData(res.data);}
      })
      .catch((e) => {
        if (!cancelled) {setError(extractErrorMessage(e, 'Failed to load the alert log'));}
      });
    return () => { cancelled = true; };
  }, []);

  if (error) {
    return <Box p={3}><Alert severity="error">{error}</Alert></Box>;
  }
  if (!data) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <DWLFLoader size="large" />
      </Box>
    );
  }

  const rows = data.deliveries || [];

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box mb={3}>
        <Typography sx={{ fontFamily: GROTESK, fontSize: { xs: '1.75rem', md: '2rem' }, fontWeight: 600, letterSpacing: '-0.03em', color: 'var(--dwlf-text)' }}>
          Alert Delivery Log
        </Typography>
        <Typography sx={{ fontFamily: MONO, fontSize: '0.85rem', color: MUTED }}>
          Every delivery the dispatcher attempted for your subscriptions — newest first, {data.meta?.retentionDays || 90}-day retention
        </Typography>
      </Box>

      {rows.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <Typography sx={{ fontFamily: MONO, fontSize: '0.85rem', color: MUTED }}>
            No deliveries recorded yet. Alerts land here the moment a subscription fires.
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={headCell}>When</TableCell>
                <TableCell sx={headCell}>Status</TableCell>
                <TableCell sx={headCell}>Channel</TableCell>
                <TableCell sx={headCell}>Event</TableCell>
                <TableCell sx={headCell}>Symbol</TableCell>
                <TableCell sx={headCell}>Detail</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((d, i) => {
                const chip = STATUS_CHIP[d.status] || { label: d.status || '?', color: 'default' };
                return (
                  <TableRow key={`${d.subscriptionId}-${d.deliveredAt}-${i}`}>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontFamily: MONO, fontSize: '0.8rem' }}>
                        {d.deliveredAt ? new Date(d.deliveredAt).toLocaleString() : '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip label={chip.label} size="small" color={chip.color} sx={{ fontWeight: 600 }} />
                    </TableCell>
                    <TableCell sx={{ textTransform: 'capitalize' }}>{d.channelType || '—'}</TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                        {getEventTypeLabel(d.eventType) || d.eventType || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>{d.symbol || '—'}</Typography>
                    </TableCell>
                    <TableCell>
                      {d.errorMessage ? (
                        <Tooltip title={d.errorMessage}>
                          <Typography variant="body2" color="error" sx={{ maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {d.errorMessage}
                          </Typography>
                        </Tooltip>
                      ) : (
                        <Typography variant="body2" color="text.secondary">—</Typography>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Container>
  );
};

export default AlertLogPage;
